// ** React Imports
import React, { useEffect, useRef, useState } from 'react'

// ** MUI Imports
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

// ** Third Party Imports
import { useDebouncedCallback } from 'use-debounce'

// ** API / Helpers
import { searchGcpServices, searchGcpSkus } from '../api'
import { formatPrice, StatusChip } from './helpers'

const GCP_CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP']
const DEFAULT_SERVICE = 'Vertex AI Search'

const tiersTooltip = (tiers, currency, unit) =>
  (tiers || []).map(t => `From ${t.startUsageAmount} ${unit || ''}: ${formatPrice(t.price, currency)}`).join('\n')

const GcpPrices = ({ refreshKey, onFetched }) => {
  // ** Services
  const [serviceQ, setServiceQ] = useState('')
  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(false)
  const [service, setService] = useState(null)

  // ** SKUs
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [currencies, setCurrencies] = useState(['USD', 'CAD'])
  const [resourceFamily, setResourceFamily] = useState('')
  const [compare, setCompare] = useState(true)
  const [issuesOnly, setIssuesOnly] = useState(false)
  const [inAppOnly, setInAppOnly] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const refreshedKey = useRef(refreshKey)

  const loadServices = useDebouncedCallback(value => {
    setServicesLoading(true)
    searchGcpServices(value, 50)
      .then(data => setServices(data.data))
      .catch(err => setError(err.message))
      .finally(() => setServicesLoading(false))
  }, 300)

  useEffect(() => {
    loadServices(serviceQ)
  }, [serviceQ])

  // ** Open on the default service
  useEffect(() => {
    searchGcpServices(DEFAULT_SERVICE, 10)
      .then(data => {
        const match = data.data.find(s => s.displayName === DEFAULT_SERVICE)
        if (!match) return
        setService(prev => prev || match)
        setServices(prev => (prev.some(s => s.serviceId === match.serviceId) ? prev : [match, ...prev]))
      })
      .catch(err => setError(err.message))
  }, [])

  const debounced = useDebouncedCallback(value => {
    setSearch(value)
    setPage(0)
  }, 400)

  useEffect(() => {
    if (!service) return
    // ** Bypass the backend cache once per "Refresh from Google" click
    const refresh = refreshKey !== refreshedKey.current
    refreshedKey.current = refreshKey
    setLoading(true)
    setError(null)
    searchGcpSkus(service.serviceId, {
      q: search,
      currency: currencies.length ? currencies : ['USD'],
      resourceFamily: resourceFamily || undefined,
      monthlyOnly: true,
      compare: compare || undefined,
      issuesOnly: compare && issuesOnly ? true : undefined,
      inAppOnly: inAppOnly || undefined,
      page: page + 1,
      limit: rowsPerPage,
      refresh: refresh || undefined
    })
      .then(data => {
        setResult(data)
        onFetched && onFetched(data.fetchedAt)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [service, search, currencies, resourceFamily, compare, issuesOnly, inAppOnly, page, rowsPerPage, refreshKey])

  const resetPage = setter => value => {
    setter(value)
    setPage(0)
  }

  const shownCurrencies = currencies.length ? currencies : ['USD']

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems='center'>
            <Grid item xs={12} md={5}>
              <Autocomplete
                size='small'
                options={services}
                loading={servicesLoading}
                value={service}
                filterOptions={x => x}
                getOptionLabel={o => o.displayName}
                isOptionEqualToValue={(o, v) => o.serviceId === v.serviceId}
                onInputChange={(e, value, reason) => reason === 'input' && setServiceQ(value)}
                onChange={(e, value) => {
                  setService(value)
                  setResourceFamily('')
                  setResult(null)
                  setPage(0)
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.serviceId}>
                    <Box>
                      <Typography variant='body2'>{o.displayName}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {o.serviceId} · {o.businessEntityName?.replace('businessEntities/', '')}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={params => (
                  <TextField {...params} label='Google Cloud service' placeholder='e.g. Compute Engine, BigQuery, Vertex AI' />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size='small'
                label='Search SKUs'
                placeholder='e.g. N2 core, storage, Gemini'
                disabled={!service}
                value={q}
                onChange={e => {
                  setQ(e.target.value)
                  debounced(e.target.value)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size='small' disabled={!service}>
                <InputLabel>Resource family</InputLabel>
                <Select
                  label='Resource family'
                  value={resourceFamily}
                  onChange={e => resetPage(setResourceFamily)(e.target.value)}
                >
                  <MenuItem value=''>All</MenuItem>
                  {(result?.resourceFamilies || []).map(f => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md='auto'>
              <ToggleButtonGroup
                size='small'
                value={currencies}
                onChange={(e, value) => resetPage(setCurrencies)(value)}
                sx={{ flexWrap: 'wrap' }}
              >
                {GCP_CURRENCIES.map(c => (
                  <ToggleButton key={c} value={c} sx={{ px: 2 }}>
                    {c}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md='auto'>
              <FormControlLabel
                control={<Switch checked={compare} onChange={e => resetPage(setCompare)(e.target.checked)} />}
                label='Compare with app prices'
              />
            </Grid>
            <Grid item xs={12} md='auto'>
              <FormControlLabel
                control={<Switch checked={inAppOnly} onChange={e => resetPage(setInAppOnly)(e.target.checked)} />}
                label='In app only'
              />
            </Grid>
            {compare && (
              <Grid item xs={12} md='auto'>
                <FormControlLabel
                  control={<Switch checked={issuesOnly} onChange={e => resetPage(setIssuesOnly)(e.target.checked)} />}
                  label='Issues only'
                />
              </Grid>
            )}
          </Grid>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 2 }}>
            Only SKUs priced per month are listed.
            {compare &&
              " SKUs are matched to app products by SKU id and compared on the first paid tier, in the SKU's usage unit."}
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity='error' sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {!service ? (
        <Alert severity='info'>Pick a Google Cloud service to list its SKUs and prices.</Alert>
      ) : loading && !result ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : result ? (
        <Card sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity .2s' }}>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Family / Group</TableCell>
                  <TableCell>Unit</TableCell>
                  {shownCurrencies.map(c => (
                    <TableCell key={c} align='right'>
                      {c}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3 + shownCurrencies.length}>No SKU matches these filters.</TableCell>
                  </TableRow>
                )}
                {result.data.map(sku => (
                  <TableRow key={sku.skuId} hover>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Typography variant='body2'>{sku.description}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {sku.skuId}
                        {sku.serviceRegions?.length ? ` · ${sku.serviceRegions.slice(0, 4).join(', ')}` : ''}
                        {sku.serviceRegions?.length > 4 ? ` +${sku.serviceRegions.length - 4}` : ''}
                      </Typography>
                      {compare && sku.appProduct && (
                        <Typography variant='caption' color='success.main' display='block'>
                          In app: {sku.appProduct.skuName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{sku.category?.resourceFamily}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {sku.category?.resourceGroup} · {sku.category?.usageType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{sku.usageUnitDescription || sku.usageUnit}</Typography>
                    </TableCell>
                    {shownCurrencies.map(c => {
                      const p = sku.prices?.[c]
                      return (
                        <TableCell key={c} align='right' sx={{ verticalAlign: 'top' }}>
                          {p ? (
                            <Tooltip
                              title={
                                <span style={{ whiteSpace: 'pre-line' }}>
                                  {tiersTooltip(p.tiers, c, sku.usageUnitDescription)}
                                </span>
                              }
                            >
                              <Typography variant='body2' sx={{ fontWeight: 600, cursor: 'help' }}>
                                {formatPrice(p.price, c)}
                                {p.tiers?.length > 1 && (
                                  <Typography component='span' variant='caption' color='text.secondary'>
                                    {' '}
                                    ({p.tiers.length} tiers)
                                  </Typography>
                                )}
                              </Typography>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                          {compare && sku.app?.[c] && sku.app[c].status !== 'not_in_app' && sku.app[c].status !== 'n/a' && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography variant='caption'>
                                App:{' '}
                                {sku.app[c].price !== null && sku.app[c].price !== undefined
                                  ? formatPrice(sku.app[c].price, c)
                                  : '—'}
                              </Typography>
                              <StatusChip app={sku.app[c]} currency={c} />
                            </Box>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={result.total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(e, value) => setPage(value)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </Card>
      ) : null}
    </Box>
  )
}

export default GcpPrices
